import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Sun, Moon } from 'lucide-react';
import { MENU } from '../navegacao';
import { useAuth } from '../contextos/AuthContext';
import { usePainel } from '../servicos/painel';

/**
 * No desktop, trilho fixo à esquerda. No celular, gaveta aberta pelo botão do
 * topo — e ela fecha sozinha ao navegar, senão a pessoa toca no item e continua
 * olhando para o menu.
 */
export function LayoutApp() {
  const [gaveta, setGaveta] = useState(false);
  const local = useLocation();
  useEffect(() => setGaveta(false), [local.pathname]);

  return (
    <div className="min-h-screen bg-papel">
      <BarraTopo aoAbrirGaveta={() => setGaveta(true)} />

      <div className="mx-auto flex max-w-[100rem] gap-0">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 overflow-y-auto border-r border-regua px-3 py-4 lg:block">
          <Trilho />
        </aside>

        {gaveta && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              aria-label="Fechar menu"
              onClick={() => setGaveta(false)}
              className="absolute inset-0 bg-black/40"
            />
            <nav className="absolute inset-y-0 left-0 w-72 overflow-y-auto bg-superficie px-3 py-4 shadow-xl">
              <div className="mb-2 flex items-center justify-between px-2">
                <span className="font-semibold">Menu</span>
                <button type="button" onClick={() => setGaveta(false)} aria-label="Fechar">
                  <X className="size-5 text-tinta-2" />
                </button>
              </div>
              <Trilho />
            </nav>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function BarraTopo({ aoAbrirGaveta }: { aoAbrirGaveta: () => void }) {
  const { perfil, sair } = useAuth();
  const [tema, setTema] = useState<'auto' | 'light' | 'dark'>('auto');

  useEffect(() => {
    const raiz = document.documentElement;
    if (tema === 'auto') raiz.removeAttribute('data-theme');
    else raiz.setAttribute('data-theme', tema);
  }, [tema]);

  return (
    <header className="sticky top-0 z-30 border-b border-regua bg-superficie/95 backdrop-blur">
      <div className="mx-auto flex max-w-[100rem] items-center gap-3 px-4 py-2.5 sm:px-6">
        <button
          type="button"
          onClick={aoAbrirGaveta}
          aria-label="Abrir menu"
          className="rounded p-1.5 text-tinta-2 hover:bg-superficie-2 lg:hidden"
        >
          <Menu className="size-5" />
        </button>

        <span className="font-semibold tracking-tight">
          Profit<span className="text-marca">Lens</span>
        </span>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTema((t) => (t === 'dark' ? 'light' : 'dark'))}
            aria-label="Alternar tema"
            className="rounded p-1.5 text-tinta-2 hover:bg-superficie-2"
          >
            {tema === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <span className="hidden text-sm text-tinta-2 sm:inline">{perfil?.nome}</span>
          <span className="hidden rounded bg-marca/10 px-1.5 py-0.5 font-mono text-[0.6rem] tracking-wider text-marca uppercase sm:inline">
            {perfil?.papel}
          </span>
          <button
            type="button"
            onClick={() => void sair()}
            aria-label="Sair"
            className="rounded p-1.5 text-tinta-2 hover:bg-superficie-2"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

function Trilho() {
  const { ehAdministrador } = useAuth();
  const { resumo } = usePainel();

  return (
    <nav className="flex flex-col gap-0.5" aria-label="Navegação principal">
      {MENU.map((grupo) => {
        const itens = grupo.itens.filter((i) => !i.soAdministrador || ehAdministrador);
        if (itens.length === 0) return null;
        return (
          <div key={grupo.id} className="flex flex-col gap-0.5">
            <span className="px-2 pt-3 pb-1 font-mono text-[0.6rem] tracking-[0.14em] text-tinta-3 uppercase">
              {grupo.rotulo}
            </span>
            {itens.map((item) => {
              // O contador só aparece quando há algo ESPERANDO por você. Nunca é
              // o total de registros: "1.284 mercadorias" não informa nada.
              const n = item.contador ? (resumo?.[item.contador] ?? 0) : 0;
              return (
                <NavLink
                  key={item.id}
                  to={item.caminho}
                  end={item.caminho === '/'}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-2.5 rounded border-l-2 px-2 py-1.5 text-sm',
                      isActive
                        ? 'border-marca bg-marca/10 font-semibold text-marca'
                        : 'border-transparent text-tinta-2 hover:bg-superficie-2',
                    ].join(' ')
                  }
                >
                  <item.icone className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{item.rotulo}</span>
                  {n > 0 && (
                    <span className="ml-auto rounded-full bg-pend/15 px-1.5 font-mono text-[0.62rem] font-semibold text-pend">
                      {n}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
